// src/modules/units/tyres/views/action/tyreactionform.js
//
// Action Capture orchestrator. Renders the form (from tyreactionform.view.js),
// validates required fields, then commits with ONE write: POST /action. The
// backend updates the tyre record (tread, wear, damage, repair type, …) from the
// action's top-level fields AND logs the action, building the details bag itself
// and stamping the server's current mileage. There is deliberately no PUT /info
// alongside it: that stored the tread measurement twice (two treadHistory
// entries per inspection) and echoed a stale tyreKm over the sensor-calculated
// one. tyres.api.js emits CHANGED on success; this form only closes back to the
// caller.
//
// The Inspect form also carries a rotation strip (fitted tyres only). It is a
// side-channel, not part of the Save: it fires its own POST /action with just a
// date, because a rotation keeps the same sensor and the system derives the new
// wheel position and on-truck km itself.

import { createTyreAction, clearTyreSensorAssociation } from '../tyres.api.js';
import {
  TYRE_ACTION,
  normalizeActionType, getActionFields, buildActionDetails, buildActionPayloadFields,
  actionLogMessage, actionVerb, apiActionType
} from '../../shared/tyre-action-schema.js';
import { hasSensor } from '../../shared/tyre-status.js';
import { todayInputValue, isoFromDateInput } from '../../shared/tyre-dates.js';
import { actionFormHTML } from './tyreactionform.view.js';

const isBlank = (v) => v === null || v === undefined || v === '';

/**
 * @param {HTMLElement} viewEl
 * @param {object} ctx
 * @param {object} ctx.tyre        the tyre being acted on
 * @param {string} ctx.actionType  inspect | repair | retread
 * @param {() => void} [ctx.onClose] called after a successful save and on cancel
 * @returns {() => void} teardown
 */
export function renderTyreActionForm(viewEl, ctx = {}) {
  let disposed = false;
  let saving = false;
  let rotating = false;
  const tyre = ctx.tyre || {};
  const type = normalizeActionType(ctx.actionType);
  const id = tyre.externalTyreId;
  const fields = getActionFields(type);

  const tpl = document.createElement('template');
  tpl.innerHTML = actionFormHTML(type, tyre);
  const root = tpl.content.firstElementChild;
  const msgEl = root.querySelector('[data-msg]');
  const submitBtn = root.querySelector('[data-submit]');
  const cancelBtn = root.querySelector('[data-cancel]');
  const submitLabel = submitBtn.textContent;

  // Rotation strip — present only for a fitted tyre on the Inspect form.
  const rotateMsg = root.querySelector('[data-rotate-msg]');
  const rotateDate = root.querySelector('[data-rotate-date]');
  const rotateSaveBtn = root.querySelector('[data-rotate-save]');
  if (rotateDate) {
    const today = todayInputValue();
    rotateDate.value = today;
    rotateDate.max = today;           // a rotation can't be in the future
  }

  viewEl.replaceChildren(root);

  function readValues() {
    const values = {};
    root.querySelectorAll('[data-field]').forEach((elm) => {
      const key = elm.getAttribute('data-field');
      // A radio group shares one data-field across every option, so reading .value
      // blindly would take whichever rendered last. Only the checked one counts,
      // and an untouched group has to stay empty so `required` still bites.
      if (elm.type === 'radio') {
        if (elm.checked) values[key] = elm.value;
        else if (!(key in values)) values[key] = '';
        return;
      }
      values[key] = elm.value;
    });
    return values;
  }

  function setMsg(text, kind) {
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    msgEl.className = 'tm-form-msg' + (kind ? ` tm-form-msg-${kind}` : '');
  }

  function setSaving(on) {
    saving = on;
    submitBtn.disabled = on;
    cancelBtn.disabled = on;
    submitBtn.textContent = on ? 'Saving…' : submitLabel;
  }

  function close() {
    if (typeof ctx.onClose === 'function') ctx.onClose();
  }

  async function submit() {
    if (saving || !id) return;
    const values = readValues();

    const missing = fields.filter((f) => f.input?.required && isBlank(values[f.key]));
    if (missing.length) {
      setMsg(`Please complete: ${missing.map((f) => f.label).join(', ')}.`, 'error');
      return;
    }

    setMsg('', '');
    setSaving(true);

    const details = buildActionDetails(type, values);

    try {
      // 0) a retirement must also free the sensor. Leaving a scrapped tyre
      // associated strands the sensor — nothing else can be fitted to it — and
      // leaves the record arguing with itself: end-of-life on one side, a live
      // association on the other. effectiveStatus() would paper over that on
      // screen, which is precisely the dual-source-of-truth trap this module
      // keeps walking away from. Fail here and nothing is logged, so the tyre is
      // not half-retired.
      if (type === TYRE_ACTION.RETIRE && hasSensor(tyre)) {
        const r0 = await clearTyreSensorAssociation(id, { reason: 'retire' });
        if (disposed) return;
        if (!r0.success) {
          setMsg(r0.message || 'Could not free the sensor, so the tyre has not been retired.', 'error');
          setSaving(false);
          return;
        }
      }

      // Log the action — this one call also updates the tyre record. Only the
      // fields the user filled in go out, at the top level under the backend's
      // names (buildActionPayloadFields); omitted fields are left untouched, so
      // current values are never re-sent. No tyreKm: the server uses its own
      // calculated mileage. Sensor/wheel are stamped like the older tyre form did,
      // so the timeline shows where the tyre was when the work was done.
      const r2 = await createTyreAction(id, {
        actionType: apiActionType(type, details),
        action: actionLogMessage(type),
        timestamp: new Date().toISOString(),
        ...buildActionPayloadFields(type, values),
        ...(isBlank(tyre.currentSensorId) ? {} : { sensorId: Number(tyre.currentSensorId) || tyre.currentSensorId }),
        ...(isBlank(tyre.currentWheelId) ? {} : { wheelId: tyre.currentWheelId })
      }, { reason: type });
      if (disposed) return;
      if (!r2.success) {
        setMsg(r2.message || 'Could not save the action.', 'error');
        setSaving(false);
        return;
      }

      setMsg(`${actionVerb(type)} saved.`, 'ok');
      setTimeout(() => { if (!disposed) close(); }, 600);
    } catch (e) {
      if (!disposed) {
        setMsg('Something went wrong while saving. Please try again.', 'error');
        setSaving(false);
      }
    }
  }

  function setRotateMsg(text, kind) {
    if (!rotateMsg) return;
    rotateMsg.textContent = text || '';
    rotateMsg.hidden = !text;
    rotateMsg.className = 'tm-form-msg' + (kind ? ` tm-form-msg-${kind}` : '');
  }

  // Independent of the inspection Save: log a rotation with just a date, then lock
  // the strip so it can't be logged twice in one session. The record's own fields
  // aren't touched (no PUT /info) — the sensor is unchanged.
  async function logRotation() {
    if (rotating || !id) return;
    const dateVal = rotateDate.value;
    if (!dateVal) { setRotateMsg('Please choose a rotation date.', 'error'); return; }

    setRotateMsg('', '');
    rotating = true;
    rotateSaveBtn.disabled = true;
    rotateSaveBtn.textContent = 'Saving…';
    try {
      const res = await createTyreAction(id, {
        actionType: apiActionType('rotate'),   // 'rotation'
        action: actionLogMessage('rotate'),    // 'Rotation logged'
        timestamp: isoFromDateInput(dateVal),
        details: {}                            // sensor & positions are server-side
      }, { reason: 'rotate' });
      if (disposed) return;
      if (!res.success) {
        setRotateMsg(res.message || 'Could not log the rotation.', 'error');
        rotating = false; rotateSaveBtn.disabled = false; rotateSaveBtn.textContent = 'Log rotation';
        return;
      }
      // one-time within this form: lock the control and confirm
      rotateDate.disabled = true;
      rotateSaveBtn.textContent = 'Rotation logged ✓';
      setRotateMsg('Rotation logged.', 'ok');
    } catch (e) {
      if (!disposed) {
        setRotateMsg('Something went wrong while logging the rotation. Please try again.', 'error');
        rotating = false; rotateSaveBtn.disabled = false; rotateSaveBtn.textContent = 'Log rotation';
      }
    }
  }

  submitBtn.addEventListener('click', submit);
  cancelBtn.addEventListener('click', () => { if (!saving) close(); });
  if (rotateSaveBtn) rotateSaveBtn.addEventListener('click', logRotation);

  return () => { disposed = true; };
}